import React from 'react';
import styles from './PeekCarousel.module.css';

export type PeekItem = { id: string; image: React.ReactNode };

export function PeekCarousel({ items }: { items: PeekItem[] }) {
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);

  // clones: [last, ...items, first]
  const loopItems = React.useMemo(() => {
    if (items.length <= 1) return items;
    return [items[items.length - 1], ...items, items[0]];
  }, [items]);

  React.useEffect(() => {
    const el = scrollerRef.current;
    if (!el || items.length <= 1) return;
    // Skip when hidden via CSS (e.g., desktop-only layout)
    if (el.offsetParent === null) return;

    // jump to first "real" item (index 1)
    requestAnimationFrame(() => {
      const firstReal = el.querySelector<HTMLElement>(`[data-idx="1"]`);
      firstReal?.scrollIntoView({
        behavior: 'auto',
        inline: 'center',
        block: 'nearest',
      });
    });
  }, [items.length]);

  React.useEffect(() => {
    const el = scrollerRef.current;
    if (!el || items.length <= 1) return;
    // Skip when hidden via CSS (e.g., desktop-only layout)
    if (el.offsetParent === null) return;

    let t: number | null = null;

    const onScroll = () => {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(() => {
        const slides = Array.from(
          el.querySelectorAll<HTMLElement>('[data-idx]'),
        );
        const rect = el.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;

        let bestIdx = 0;
        let bestDist = Infinity;
        slides.forEach((s) => {
          const r = s.getBoundingClientRect();
          const c = r.left + r.width / 2;
          const d = Math.abs(c - centerX);
          if (d < bestDist) {
            bestDist = d;
            bestIdx = Number(s.dataset.idx);
          }
        });

        // if snapped to clone ends, jump (no animation) to matching real slide
        if (bestIdx === 0) {
          const lastReal = el.querySelector<HTMLElement>(
            `[data-idx="${items.length}"]`,
          );
          lastReal?.scrollIntoView({
            behavior: 'auto',
            inline: 'center',
            block: 'nearest',
          });
        } else if (bestIdx === items.length + 1) {
          const firstReal = el.querySelector<HTMLElement>(`[data-idx="1"]`);
          firstReal?.scrollIntoView({
            behavior: 'auto',
            inline: 'center',
            block: 'nearest',
          });
        }
      }, 80);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (t) window.clearTimeout(t);
    };
  }, [items.length]);

  return (
    <div className={styles.viewport}>
      <div ref={scrollerRef} className={styles.scroller}>
        {loopItems.map((item, idx) => (
          <div
            key={`${item.id}-${idx}`}
            data-idx={idx}
            className={styles.slide}
          >
            <div className={styles.frame}>{item.image}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
